<template>
<div class="welcome-page">
  <div class="logo-area">
    <img src="../assets/iroquoise_logo_rvb.png" alt="L'Iroquoise" class="welcome-logo" />
  </div>
  <div class="hw-card">
    <h1 class="hw-title">Bienvenue</h1>
    <p class="hw-subtitle">Commençons par vos coordonnées</p>

    <div v-if="config && config.showInfoBlock" class="info-banner">
      <i class="pi pi-info-circle info-banner-icon"></i>
      <div v-if="sanitizedInfoHtml" v-html="sanitizedInfoHtml"></div>
    </div>

    <div class="form-fields">
      <div class="form-group">
        <label for="firstname" class="form-label">Prénom</label>
        <InputText
          id="firstname"
          v-model="firstname"
          @keyup.enter="nextPage()"
          placeholder="Votre prénom"
          :class="{ 'p-invalid': validationErrors.firstname && submitted }"
          class="form-input"
        />
        <small v-show="validationErrors.firstname && submitted" class="form-error">
          Merci d'indiquer votre prénom
        </small>
      </div>

      <div class="form-group">
        <label for="lastname" class="form-label">Nom</label>
        <InputText
          id="lastname"
          v-model="lastname"
          @keyup.enter="nextPage()"
          placeholder="Votre nom de famille"
          :class="{ 'p-invalid': validationErrors.lastname && submitted }"
          class="form-input"
        />
        <small v-show="validationErrors.lastname && submitted" class="form-error">
          Merci d'indiquer votre nom de famille.
        </small>
      </div>

      <div class="form-group">
        <label for="phone" class="form-label">Numéro de téléphone</label>
        <InputText
          id="phone"
          v-model="phone"
          @keyup.enter="nextPage()"
          placeholder="079 123 45 67"
          :class="{ 'p-invalid': validationErrors.phone && submitted }"
          class="form-input"
        />
        <small v-show="validationErrors.phone && submitted" class="form-error">
          Veuillez entrer un numéro de téléphone suisse valide au format 079/999.99.99 ou 0799999999
        </small>
      </div>
    </div>

    <p class="privacy-note">
      <i class="pi pi-lock"></i>
      Les informations collectées sur ce site sont stockées et exclusivement utilisées par l'iroquoise pour le traitement des rendez-vous. Elles ne sont jamais transmises à des tiers.
    </p>
  </div>

  <div class="hw-actions">
    <button class="hw-btn hw-btn-primary" @click="nextPage()">
      Suivant
      <i class="pi pi-arrow-right"></i>
    </button>
  </div>
</div>
</template>

<style scoped>
.welcome-page {
  text-align: left;
}

.logo-area {
  text-align: center;
  margin-bottom: 1.25rem;
}

.welcome-logo {
  width: 140px;
  height: auto;
}

.info-banner {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
  color: #1e40af;
  line-height: 1.5;
}

.info-banner-icon {
  font-size: 1.1rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.form-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
}

.form-input {
  width: 100%;
}

.form-error {
  color: #dc2626;
  font-size: 0.8rem;
}

.privacy-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.78rem;
  color: #94a3b8;
  margin-top: 1.25rem;
  margin-bottom: 0;
  line-height: 1.5;
}

.privacy-note .pi {
  margin-top: 0.1rem;
  flex-shrink: 0;
}
</style>

<script setup>
import { defineEmits, ref, onMounted, computed } from 'vue'

const emit = defineEmits(['next-page', 'previous-page', 'go-home', 'go-confirm']);

const firstname = ref("");
const lastname = ref("");
const phone = ref("");

const validationErrors = ref({});

var submitted = ref(false)

import { useMainStore } from '../store'
import axios from 'axios'

const store = useMainStore();

const config = ref({ showInfoBlock: false, infoBlockHtml: "" })

const sanitizedInfoHtml = computed(() => sanitizeInfoHtml(config.value?.infoBlockHtml || ""));

onMounted(async () => {
  try {
    const response = await axios.get(`${process.env.VUE_APP_API_URL}/Settings/public-appointments-info`);
    config.value = {
        showInfoBlock: response.data.showInfoBlock === true || response.data.showInfoBlock === "true",
        infoBlockHtml: response.data.infoBlockHtml || ""
    };
  } catch (error) {
    try {
      const configUrl = process.env.VUE_APP_CONFIG_URL || '/config.json';
      const response = await axios.get(configUrl)
      config.value = {
        showInfoBlock: !!response.data.showInfoBlock,
        infoBlockHtml: response.data.infoBlockHtml || response.data.infoBlockText || ""
      };
    } catch (fallbackError) {
      console.error('Failed to fetch appointment info settings:', fallbackError)
    }
  }
})

function sanitizeInfoHtml(html) {
    const parser = new DOMParser();
    const parsed = parser.parseFromString(html || "", 'text/html');
    const allowedTags = new Set(["B", "STRONG", "I", "EM", "BR", "P", "DIV", "UL", "OL", "LI"]);

    const walk = (node) => {
        const children = [...node.childNodes];
        children.forEach((child) => {
            if (child.nodeType === 1) {
                if (!allowedTags.has(child.tagName)) {
                    child.replaceWith(...child.childNodes);
                } else {
                    [...child.attributes].forEach((attr) => child.removeAttribute(attr.name));
                    walk(child);
                }
            } else if (child.nodeType !== 3) {
                child.remove();
            }
        });
    };

    walk(parsed.body);
    return parsed.body.innerHTML;
}

function nextPage() {
    submitted.value = true;
    if(validateForm())
    {
        phone.value = phone.value.replace(/ /g, '').replace(/\//g, '').replace(/\./g, '');
        console.log("phone : " + phone.value);
        store.$patch((state) => {
            state.clientData = {firstname : firstname, lastname: lastname, phone : phone}
        });
          axios
      .get(`${process.env.VUE_APP_API_URL}/Appointments/wakeup`)
      .then(response => {
          console.log("woke up API : " + response.data);
      });

    emit('next-page', {pageIndex: 0});
    }
}

function validateForm() {
            if (!firstname.value.trim())
                validationErrors.value['firstname'] = true;
            else
                delete validationErrors.value['firstname'];

            if (!lastname.value.trim())
                validationErrors.value['lastname'] = true;
            else
                delete validationErrors.value['lastname'];

            var form1 = /^\d{10}$/;
            var form2 = /^\d{3}\/\d{3}\.\d{2}\.\d{2}$/;
            var form3 = /^\d{3}\s\d{3}\s\d{2}\s\d{2}$/;

            if (!phone.value.trim().match(form1) && !phone.value.trim().match(form2) && !phone.value.trim().match(form3))
            {
                validationErrors.value['phone'] = true;
            }
            else
            {
                delete validationErrors.value['phone'];
            }

            return !Object.keys(validationErrors.value).length;
}
</script>
